import { useEffect, useState, useCallback } from 'react';
import { homeAPI, postsAPI } from '../services/api';
import { Card, Btn, Select, Spinner, useToast } from '../components/UI';

const TAB_OPTIONS = [
  { key: 'tab_0', label: 'Sheikh Bava Usthad' },
  { key: 'tab_1', label: 'Sheikh Yusuf Sulthan' },
];

export default function HomeTabs() {
  const { show, ToastEl } = useToast();

  const [tabs, setTabs]             = useState([]);
  const [posts, setPosts]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);

  // Form state
  const [selTab, setSelTab]           = useState('');
  const [selCategory, setSelCategory] = useState('');
  const [selPost, setSelPost]         = useState('');
  const [saving, setSaving]           = useState(false);
  const [removing, setRemoving]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tabsRes, postsRes, catsRes] = await Promise.all([
        homeAPI.getTabs(),
        postsAPI.list({ limit: 200, status: 'published' }),
        postsAPI.listCategories(),
      ]);
      setTabs(tabsRes.data.data);
      setPosts(postsRes.data.data);
      setCategories(catsRes.data.data);
    } catch { show('Failed to load data', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Derived
  const assignedTabKeys = tabs.filter(t => t.post_id != null).map(t => t.tab_key);
  const assignedPostIds = tabs.filter(t => t.post_id != null).map(t => t.post_id);
  const assignedRows    = tabs.filter(t => t.post_id != null);
  const allAssigned     = assignedTabKeys.length === TAB_OPTIONS.length;

  const filteredPosts = selCategory
    ? posts.filter(p => String(p.category_id) === selCategory)
    : posts;

  async function assign() {
    if (!selTab || !selPost) return;
    setSaving(true);
    try {
      await homeAPI.updateTab(selTab, { post_id: parseInt(selPost) });
      show('Assignment saved');
      setSelTab('');
      setSelCategory('');
      setSelPost('');
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to assign', 'error');
    } finally { setSaving(false); }
  }

  async function remove(tabKey) {
    setRemoving(tabKey);
    try {
      await homeAPI.updateTab(tabKey, { post_id: null });
      show('Assignment removed');
      load();
    } catch { show('Failed to remove', 'error'); }
    finally { setRemoving(null); }
  }

  return (
    <div style={{ padding: 28 }}>
      {ToastEl}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Home Tabs</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text3)', fontSize: 13 }}>
          Assign a published post to each spiritual lineage tab on the home screen.
          The tab will show the first few lines of the post with a "Read more" link.
        </p>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}><Spinner size={28}/></div>
      ) : (
        <>
          {/* ── Assignment Form ───────────────────────────────── */}
          {!allAssigned && (
            <Card style={{ padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: 'var(--text2)' }}>
                Assign a Post to a Tab
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>

                {/* Dropdown 1 — Tab */}
                <div>
                  <label style={labelStyle}>Tab</label>
                  <Select value={selTab} onChange={e => setSelTab(e.target.value)} style={{ width: '100%' }}>
                    <option value="">Select tab…</option>
                    {TAB_OPTIONS.map(t => (
                      <option key={t.key} value={t.key} disabled={assignedTabKeys.includes(t.key)}>
                        {t.label}{assignedTabKeys.includes(t.key) ? '  (assigned)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Dropdown 2 — Category filter */}
                <div>
                  <label style={labelStyle}>Post Category</label>
                  <Select
                    value={selCategory}
                    onChange={e => { setSelCategory(e.target.value); setSelPost(''); }}
                    style={{ width: '100%' }}
                  >
                    <option value="">All categories</option>
                    {categories.map(c => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </Select>
                </div>

                {/* Dropdown 3 — Post */}
                <div>
                  <label style={labelStyle}>Post Title</label>
                  <Select value={selPost} onChange={e => setSelPost(e.target.value)} style={{ width: '100%' }}>
                    <option value="">Select post…</option>
                    {filteredPosts.map(p => (
                      <option key={p.id} value={String(p.id)} disabled={assignedPostIds.includes(p.id)}>
                        {p.title}{assignedPostIds.includes(p.id) ? '  (assigned)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Post preview */}
              {selPost && (() => {
                const post = posts.find(p => String(p.id) === selPost);
                const preview = post?.description || '';
                return preview ? (
                  <div style={{
                    padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8,
                    fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 14,
                    display: '-webkit-box', WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {preview}
                  </div>
                ) : null;
              })()}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn onClick={assign} disabled={!selTab || !selPost || saving}>
                  {saving ? <Spinner size={14}/> : 'Assign'}
                </Btn>
              </div>
            </Card>
          )}

          {allAssigned && (
            <div style={{
              padding: '10px 16px', borderRadius: 8, marginBottom: 20,
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              fontSize: 13, color: '#166534',
            }}>
              Both tabs are assigned. Remove an assignment below to reassign.
            </div>
          )}

          {/* ── Assigned rows ────────────────────────────────── */}
          {assignedRows.length > 0 ? (
            <Card>
              <div style={{
                padding: '12px 20px', borderBottom: '1px solid var(--border)',
                fontSize: 11, fontWeight: 600, color: 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '.06em',
                display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 16,
              }}>
                <span>Tab</span><span>Assigned Post</span><span/>
              </div>
              {assignedRows.map((row, i) => {
                const tabLabel = TAB_OPTIONS.find(t => t.key === row.tab_key)?.label ?? row.tab_key;
                return (
                  <div key={row.tab_key} style={{
                    display: 'grid', gridTemplateColumns: '200px 1fr auto',
                    gap: 16, alignItems: 'center',
                    padding: '14px 20px',
                    borderBottom: i < assignedRows.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                        {tabLabel}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                        {row.tab_key}
                      </div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>
                        {row.post_title}
                      </div>
                      {row.excerpt && (
                        <div style={{
                          fontSize: 12, color: 'var(--text3)', lineHeight: 1.5,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {row.excerpt}
                        </div>
                      )}
                    </div>
                    <Btn
                      size="sm" variant="danger"
                      disabled={removing === row.tab_key}
                      onClick={() => remove(row.tab_key)}
                    >
                      {removing === row.tab_key ? <Spinner size={13}/> : 'Remove'}
                    </Btn>
                  </div>
                );
              })}
            </Card>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 13 }}>
              No tabs assigned yet. Use the form above to assign posts.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  marginBottom: 5,
  color: 'var(--text3)',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
};
